import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dec, formatMoney } from '@makila/fiscal';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, HeartPulse, MessageCircle, Plus, Repeat, X } from 'lucide-react';
import clsx from 'clsx';
import { type AgendaClient, type AgendaResource, type AgendaService, type AgendaStaff, type Appointment, type ApptStatus, STATUS_LABEL } from './types';

const HOUR = 64; // px por hora (15 min = 16 px: alvo de toque com folga)
const MZ = '+02:00';
const iso = (date: string, min: number) => `${date}T${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}:00${MZ}`;
const localDate = (d: Date) => new Date(d.getTime() + 2 * 3_600_000).toISOString().slice(0, 10);
const localMin = (s: string) => { const d = new Date(new Date(s).getTime() + 2 * 3_600_000); return d.getUTCHours() * 60 + d.getUTCMinutes(); };
const hm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const addDays = (date: string, n: number) => { const d = new Date(`${date}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const weekday = (date: string) => new Date(`${date}T12:00:00Z`).getUTCDay();
const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const longDate = (date: string) => `${DAYS[weekday(date)]}, ${Number(date.slice(8))} de ${MONTHS[Number(date.slice(5, 7)) - 1]}`;
const toHex = (c: string | null | undefined) => (c && /^#[0-9a-f]{6}$/i.test(c) ? c : '#64748b');
const waLink = (n: string, text: string) => `https://wa.me/${n.replace(/\D/g, '').replace(/^(8\d{8})$/, '258$1')}?text=${encodeURIComponent(text)}`;
const LIVE: ApptStatus[] = ['booked', 'confirmed', 'arrived', 'in_service', 'done'];

/**
 * Marcações que se sobrepõem ficam lado a lado: cada uma numa faixa, e cada
 * grupo de sobrepostas divide a largura pelo número de faixas que usa.
 */
function lanes(list: Appointment[]): { a: Appointment; lane: number; of: number }[] {
  const sorted = [...list].sort((x, y) => x.startsAt.localeCompare(y.startsAt) || y.endsAt.localeCompare(x.endsAt));
  const out: { a: Appointment; lane: number; of: number }[] = [];
  let group: typeof out = [];
  let groupEnd = '';
  let ends: string[] = [];
  const flush = () => { const n = Math.max(1, ends.length); for (const g of group) g.of = n; group = []; ends = []; };
  for (const a of sorted) {
    if (group.length && a.startsAt >= groupEnd) flush();
    let lane = ends.findIndex((e) => e <= a.startsAt);
    if (lane < 0) { lane = ends.length; ends.push(a.endsAt); } else ends[lane] = a.endsAt;
    const item = { a, lane, of: 1 };
    group.push(item); out.push(item);
    groupEnd = group.length === 1 || a.endsAt > groupEnd ? a.endsAt : groupEnd;
  }
  flush();
  return out;
}

interface Column { key: string; date: string; staffId: string | null; resourceId: string | null; title: React.ReactNode; schedule: { from: string; to: string }[] | null }

export interface AgendaViewProps {
  client: AgendaClient;
  staff: AgendaStaff[];
  services: AgendaService[];
  resources?: AgendaResource[];
  businessName: string;
  /** Sem rede (caixa): só de leitura, com a última agenda que se viu. */
  offline?: boolean;
  /** Escolher o cliente da ficha (a caixa usa o diálogo dela); sem isto, nome e telefone. */
  pickCustomer?: () => Promise<{ id?: string; name: string; phone: string | null } | null>;
  /** Caixa: "Cobrar" abre a venda com os serviços da marcação. */
  onCharge?: (a: Appointment) => void;
  /** Ficha de saúde do cliente (módulo; só gerentes e profissionais). */
  onHealth?: (a: Appointment) => void;
  /** Guardar a última agenda vista (caixa, para ver sem rede). */
  cache?: { get(key: string): Promise<Appointment[] | undefined>; set(key: string, v: Appointment[]): Promise<void> };
}

/**
 * Agenda (docs/14, Fase 1): dia por profissional (ou por sala), semana de um
 * profissional; tocar num espaço livre marca, arrastar muda a hora ou o
 * profissional, tocar numa marcação mostra as acções do estado dela.
 */
export function AgendaView(p: AgendaViewProps) {
  const [date, setDate] = useState(() => localDate(new Date()));
  const [mode, setMode] = useState<'day' | 'week'>('day');
  const [by, setBy] = useState<'staff' | 'resource'>('staff');
  const [weekStaff, setWeekStaff] = useState<string | null>(null);
  const [items, setItems] = useState<Appointment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [opened, setOpened] = useState<Appointment | null>(null);
  const [creating, setCreating] = useState<{ staffId: string | null; resourceId: string | null; startsAt: string } | null>(null);
  const [now, setNow] = useState(Date.now());
  const scroller = useRef<HTMLDivElement>(null);

  const from = mode === 'day' ? date : addDays(date, -((weekday(date) + 6) % 7));
  const to = mode === 'day' ? date : addDays(from, 6);
  const load = useCallback(async () => {
    const key = `agenda:${from}:${to}`;
    try {
      const list = await p.client.list(from, to);
      setItems(list); setError(null);
      void p.cache?.set(key, list);
    } catch (e) {
      const cached = await p.cache?.get(key);
      if (cached) setItems(cached);
      setError((e as Error).message);
    }
  }, [p.client, p.cache, from, to]);
  useEffect(() => { void load(); }, [load]);
  // Outras caixas e a gestão também marcam: relê de 30 em 30 s.
  useEffect(() => { const t = setInterval(() => { setNow(Date.now()); void load(); }, 30_000); return () => clearInterval(t); }, [load]);

  const staffById = useMemo(() => new Map(p.staff.map((s) => [s.id, s])), [p.staff]);
  const wStaff = weekStaff ?? p.staff[0]?.id ?? null;
  const columns: Column[] = mode === 'day'
    ? (by === 'resource' && p.resources?.length
      ? p.resources.map((r) => ({ key: r.id, date, staffId: null, resourceId: r.id, title: r.name, schedule: null }))
      : p.staff.map((s) => ({
        key: s.id, date, staffId: s.id, resourceId: null, schedule: s.schedule?.length ? s.schedule.filter((x) => x.day === weekday(date)) : null,
        title: <span className="flex items-center gap-2"><Dot s={s} />{s.name}</span>,
      })))
    : Array.from({ length: 7 }, (_, k) => {
      const d = addDays(from, k);
      const s = wStaff ? staffById.get(wStaff) : undefined;
      return { key: d, date: d, staffId: wStaff, resourceId: null, schedule: s?.schedule?.length ? s.schedule.filter((x) => x.day === weekday(d)) : null, title: <span>{DAYS[weekday(d)]!.slice(0, 3)} {Number(d.slice(8))}</span> };
    });

  // Janela de horas: das 7 às 21, alargada aos horários da equipa.
  const allSlots = p.staff.flatMap((s) => s.schedule ?? []);
  const startH = Math.min(7, ...allSlots.map((s) => Number(s.from.slice(0, 2))));
  const endH = Math.max(21, ...allSlots.map((s) => Math.ceil(Number(s.to.slice(0, 2)) + Number(s.to.slice(3)) / 60)));
  const top = (min: number) => ((min - startH * 60) / 60) * HOUR;
  useEffect(() => {
    // Abre perto da hora actual (ou das 8 h).
    const n = localDate(new Date()) === date ? localMin(new Date().toISOString()) - 60 : 8 * 60;
    scroller.current?.scrollTo({ top: Math.max(0, top(n)) });
  }, [date, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const inColumn = (c: Column) => items.filter((a) => localDate(new Date(a.startsAt)) === c.date && (c.resourceId ? a.resourceId === c.resourceId : a.staffId === c.staffId) && a.status !== 'cancelled');

  // ── Arrastar uma marcação (muda a hora e, na vista de dia, o profissional) ──
  const drag = useRef<{ a: Appointment; y0: number; x0: number; moved: boolean } | null>(null);
  const [ghost, setGhost] = useState<{ id: string; dMin: number; dCol: number } | null>(null);
  const colWidth = () => (scroller.current?.querySelector('[data-col]') as HTMLElement | null)?.offsetWidth ?? 200;
  const onDown = (e: React.PointerEvent, a: Appointment) => {
    if (p.offline || ['done', 'in_service', 'no_show'].includes(a.status)) return;
    drag.current = { a, y0: e.clientY, x0: e.clientX, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dy = e.clientY - d.y0, dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dy) < 6 && Math.abs(dx) < 6) return;
    d.moved = true;
    setGhost({ id: d.a.id, dMin: Math.round((dy / HOUR) * 60 / 15) * 15, dCol: mode === 'day' && by === 'staff' ? Math.round(dx / colWidth()) : 0 });
  };
  const justDragged = useRef(false);
  const onUp = async (a: Appointment) => {
    const d = drag.current;
    drag.current = null;
    const g = ghost;
    setGhost(null);
    if (!d?.moved) return; // um toque: abre pelo onClick (também com o teclado)
    justDragged.current = true;
    setTimeout(() => { justDragged.current = false; }, 0);
    if (!g || (!g.dMin && !g.dCol)) return;
    const col = columns.findIndex((c) => c.staffId === a.staffId);
    const target = columns[Math.min(columns.length - 1, Math.max(0, col + g.dCol))];
    const startsAt = new Date(new Date(a.startsAt).getTime() + g.dMin * 60_000).toISOString();
    await change(a, { startsAt, ...(target?.staffId && target.staffId !== a.staffId ? { staffId: target.staffId } : {}) });
  };

  /** Alterar com rede; se a hora já está ocupada ou fora do horário, oferece marcar na mesma (gerente). */
  const change = async (a: Appointment, patch: Parameters<AgendaClient['update']>[1]) => {
    try {
      await p.client.update(a.id, patch); setError(null); await load(); return true;
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (['slot_taken', 'outside_schedule', 'resource_taken'].includes(code ?? '') && p.client.approve && await p.client.approve((e as Error).message)) {
        try { await p.client.update(a.id, { ...patch, force: true }); await load(); return true; } catch (e2) { setError((e2 as Error).message); return false; }
      }
      setError((e as Error).message); return false;
    }
  };

  const nowMin = localMin(new Date(now).toISOString());
  const todayStr = localDate(new Date(now));

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Barra: data, vista, nova marcação */}
      <div className="flex flex-wrap items-center gap-2 border-b border-base bg-[var(--surface)] px-4 py-2.5">
        <div className="flex items-center gap-1">
          <button aria-label={mode === 'day' ? 'Dia anterior' : 'Semana anterior'} onClick={() => setDate(addDays(date, mode === 'day' ? -1 : -7))} className="grid h-12 w-12 place-items-center rounded-xl hover:bg-surface-2"><ChevronLeft /></button>
          <button onClick={() => setDate(localDate(new Date()))} className="h-12 rounded-xl border border-base px-4 font-semibold hover:bg-surface-2">Hoje</button>
          <button aria-label={mode === 'day' ? 'Dia seguinte' : 'Semana seguinte'} onClick={() => setDate(addDays(date, mode === 'day' ? 1 : 7))} className="grid h-12 w-12 place-items-center rounded-xl hover:bg-surface-2"><ChevronRight /></button>
        </div>
        <span className="min-w-0 truncate font-display text-lg font-semibold">{mode === 'day' ? longDate(date) : `${Number(from.slice(8))} a ${Number(to.slice(8))} de ${MONTHS[Number(to.slice(5, 7)) - 1]}`}</span>
        <label className="relative grid h-12 w-12 shrink-0 cursor-pointer place-items-center rounded-xl hover:bg-surface-2" title="Escolher o dia">
          <CalendarDays size={20} className="text-muted" />
          <input type="date" aria-label="Escolher o dia" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" />
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {mode === 'week' && (
            <select aria-label="Profissional da semana" value={wStaff ?? ''} onChange={(e) => setWeekStaff(e.target.value)} className="h-12 rounded-xl border border-base bg-[var(--surface)] px-3">
              {p.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {mode === 'day' && !!p.resources?.length && <Seg value={by} onChange={setBy} options={[['staff', 'Profissionais'], ['resource', 'Salas']]} />}
          <Seg value={mode} onChange={setMode} options={[['day', 'Dia'], ['week', 'Semana']]} />
          <button disabled={p.offline} onClick={() => setCreating({ staffId: p.staff[0]?.id ?? null, resourceId: null, startsAt: iso(date, Math.max(nowMin + 15 - (nowMin % 15), 9 * 60)) })}
            className="flex h-12 items-center gap-2 rounded-xl bg-brand-700 px-4 font-bold text-white disabled:opacity-40"><Plus size={18} /> Marcação</button>
        </div>
      </div>
      {(error || p.offline) && (
        <div role="alert" className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-sm text-amber-900">
          <span className="flex-1">{p.offline ? 'Sem rede: a agenda mostra a última versão recebida; as marcações novas precisam de internet.' : error}</span>
          {error && <button aria-label="Fechar aviso" onClick={() => setError(null)} className="grid h-9 w-9 place-items-center"><X size={16} /></button>}
        </div>
      )}
      {notice && <div role="status" className="flex items-center gap-2 bg-sky-50 px-4 py-2 text-sm text-sky-900 dark:bg-sky-950 dark:text-sky-100"><span className="flex-1">{notice}</span><button aria-label="Fechar aviso" onClick={() => setNotice(null)} className="grid h-9 w-9 place-items-center"><X size={16} /></button></div>}

      {!columns.length ? (
        <div className="grid flex-1 place-items-center p-6 text-center text-muted"><p className="max-w-md">Ainda não há profissionais. Na gestão, em Equipa, abra a ficha de cada um e ligue "Faz serviços".</p></div>
      ) : (
        <div ref={scroller} className="min-h-0 flex-1 overflow-auto">
          {/* Cabeçalho das colunas (fixo ao rolar) */}
          <div className="sticky top-0 z-20 flex border-b border-base bg-[var(--surface)]" style={{ minWidth: 56 + columns.length * 180 }}>
            <div className="w-14 shrink-0" />
            {columns.map((c) => (
              <div key={c.key} className={clsx('min-w-[180px] flex-1 border-l border-base px-3 py-2.5 text-sm font-semibold', c.date === todayStr && mode === 'week' && 'text-brand-700')}>{c.title}</div>
            ))}
          </div>
          <div className="relative flex" style={{ minWidth: 56 + columns.length * 180, height: (endH - startH) * HOUR }}>
            {/* Horas */}
            <div className="w-14 shrink-0">
              {Array.from({ length: endH - startH }, (_, h) => (
                <div key={h} className="num relative text-right text-xs text-muted" style={{ height: HOUR }}><span className="absolute -top-2 right-2">{hm((startH + h) * 60)}</span></div>
              ))}
            </div>
            {columns.map((c) => {
              const closed = c.schedule !== null; // com horário definido, fora dele fica sombreado
              return (
                <div key={c.key} data-col className="relative min-w-[180px] flex-1 border-l border-base"
                  onClick={(e) => {
                    if (p.offline || e.target !== e.currentTarget) return;
                    const y = e.nativeEvent.offsetY;
                    const min = startH * 60 + Math.floor((y / HOUR) * 4) * 15;
                    setCreating({ staffId: c.staffId ?? p.staff[0]?.id ?? null, resourceId: c.resourceId, startsAt: iso(c.date, min) });
                  }}>
                  {/* Linhas de 30 min e horas fora do horário */}
                  {Array.from({ length: (endH - startH) * 2 }, (_, k) => <div key={k} className={clsx('pointer-events-none border-b', k % 2 ? 'border-[var(--border)]' : 'border-[var(--border)]/40')} style={{ height: HOUR / 2 }} />)}
                  {closed && <OffHours schedule={c.schedule!} startH={startH} endH={endH} />}
                  {c.date === todayStr && nowMin >= startH * 60 && nowMin <= endH * 60 && (
                    <div className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-brand-500" style={{ top: top(nowMin) }}><span className="absolute -left-1 -top-1.5 h-3 w-3 rounded-full bg-brand-500" /></div>
                  )}
                  {lanes(inColumn(c)).map(({ a, lane, of }) => {
                    const s = staffById.get(a.staffId);
                    const st = localMin(a.startsAt), en = localMin(a.endsAt);
                    const g = ghost?.id === a.id ? ghost : null;
                    const color = toHex(s?.color);
                    return (
                      <button key={a.id} aria-label={`${hm(st)} ${a.customerName}`}
                        onClick={() => { if (!justDragged.current) setOpened(a); }}
                        onPointerDown={(e) => onDown(e, a)} onPointerMove={onMove} onPointerUp={() => void onUp(a)}
                        className={clsx('absolute z-10 flex touch-none flex-col overflow-hidden rounded-lg border-l-4 px-2 py-1 text-left text-xs shadow-xs transition-shadow hover:shadow-md',
                          a.status === 'no_show' && 'opacity-50 line-through', a.status === 'done' && 'opacity-70', g && 'z-30 opacity-80 shadow-lg ring-2 ring-brand-500')}
                        style={{ left: `calc(${(lane / of) * 100}% + 4px)`, width: `calc(${100 / of}% - 8px)`, top: top(st) + 1 + (g?.dMin ?? 0) * HOUR / 60, height: Math.max(22, ((en - st) / 60) * HOUR - 2), borderLeftColor: color, background: `${color}1f`, transform: g?.dCol ? `translateX(${g.dCol * 100}%)` : undefined }}>
                        <span className="num font-semibold">{hm(st + (g?.dMin ?? 0))}–{hm(en + (g?.dMin ?? 0))} <span className="font-normal text-muted">· {STATUS_LABEL[a.status]}</span></span>
                        <span className="truncate text-sm font-semibold">{a.customerName}</span>
                        <span className="truncate text-muted">{a.services.map((x) => x.description).join(', ')}{by === 'resource' && s ? ` · ${s.name}` : ''}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {creating && (
        <NewAppointment {...p} initial={creating} onClose={() => setCreating(null)}
          onDone={(msg) => { setCreating(null); void load(); if (msg) setNotice(msg); }} />
      )}
      {opened && (
        <AppointmentDialog a={opened} staff={staffById.get(opened.staffId)} businessName={p.businessName} offline={p.offline} onCharge={p.onCharge} onHealth={p.onHealth}
          onClose={() => setOpened(null)}
          onChange={async (patch) => { const ok = await change(opened, patch); if (ok) setOpened(null); }} />
      )}
    </div>
  );
}

function Dot({ s }: { s: AgendaStaff }) {
  return s.photo ? <img src={s.photo} alt="" className="h-7 w-7 rounded-full object-cover" /> : <span aria-hidden className="grid h-7 w-7 place-items-center rounded-full text-xs font-bold text-white" style={{ background: toHex(s.color) }}>{s.name.slice(0, 1).toUpperCase()}</span>;
}

function Seg<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: [T, string][] }) {
  return (
    <div role="radiogroup" className="inline-grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-surface-2 p-1">
      {options.map(([v, l]) => <button key={v} role="radio" aria-checked={value === v} onClick={() => onChange(v)} className={clsx('h-10 rounded-lg px-3 text-sm font-semibold', value === v ? 'bg-[var(--surface)] shadow-sm' : 'text-muted')}>{l}</button>)}
    </div>
  );
}

/** Sombras nas horas em que o profissional não atende. */
function OffHours({ schedule, startH, endH }: { schedule: { from: string; to: string }[]; startH: number; endH: number }) {
  const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3));
  const open = [...schedule].map((s) => [toMin(s.from), toMin(s.to)] as const).sort((a, b) => a[0] - b[0]);
  const blocks: [number, number][] = [];
  let cur = startH * 60;
  for (const [a, b] of open) { if (a > cur) blocks.push([cur, a]); cur = Math.max(cur, b); }
  if (cur < endH * 60) blocks.push([cur, endH * 60]);
  return <>{blocks.map(([a, b]) => <div key={a} aria-hidden className="pointer-events-none absolute inset-x-0 bg-[repeating-linear-gradient(135deg,transparent,transparent_6px,rgb(0_0_0/.04)_6px,rgb(0_0_0/.04)_12px)] dark:bg-[repeating-linear-gradient(135deg,transparent,transparent_6px,rgb(255_255_255/.05)_6px,rgb(255_255_255/.05)_12px)]" style={{ top: ((a - startH * 60) / 60) * HOUR, height: ((b - a) / 60) * HOUR }} />)}</>;
}

function Shell({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog ref={ref} onCancel={(e) => { e.preventDefault(); onClose(); }} className="m-0 h-full max-h-none w-full max-w-none bg-[var(--surface)] p-0 text-[var(--text)] sm:m-auto sm:h-auto sm:max-h-[92vh] sm:w-[min(680px,96vw)] sm:rounded-2xl">
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-base px-5 py-3">
          <h2 className="text-xl font-bold">{title}</h2>
          <button aria-label="Fechar" onClick={onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
        {footer && <div className="border-t border-base p-4">{footer}</div>}
      </div>
    </dialog>
  );
}

/** Nova marcação: cliente → serviços (soma a duração) → profissional → hora; opcionalmente repetida. */
function NewAppointment(p: AgendaViewProps & { initial: { staffId: string | null; resourceId: string | null; startsAt: string }; onClose: () => void; onDone: (msg?: string) => void }) {
  const [customer, setCustomer] = useState<{ id?: string; name: string; phone: string | null }>({ name: '', phone: null });
  const [staffId, setStaffId] = useState(p.initial.staffId ?? p.staff[0]?.id ?? '');
  const [resourceId, setResourceId] = useState<string | null>(p.initial.resourceId);
  const [day, setDay] = useState(localDate(new Date(p.initial.startsAt)));
  const [time, setTime] = useState(hm(localMin(p.initial.startsAt)));
  const [chosen, setChosen] = useState<string[]>([]);
  const [minutes, setMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [repeat, setRepeat] = useState({ on: false, everyWeeks: 1, times: 4 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = p.staff.find((x) => x.id === staffId);
  const doable = p.services.filter((x) => !s?.services?.length || s.services.some((v) => v.productId === x.id));
  const own = (id: string) => s?.services?.find((v) => v.productId === id);
  const total = chosen.reduce((t, id) => t + (own(id)?.durationMin ?? p.services.find((x) => x.id === id)?.durationMin ?? 30), 0);
  const submit = async (force = false) => {
    setBusy(true); setError(null);
    try {
      const r = await p.client.create({
        customer: { ...customer, name: customer.name.trim() }, staffId, resourceId, startsAt: `${day}T${time}:00${MZ}`,
        services: chosen.map((productId) => ({ productId })), ...(minutes ? { durationMin: Number(minutes) } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(repeat.on ? { repeat: { everyWeeks: repeat.everyWeeks, times: repeat.times } } : {}), ...(force ? { force: true } : {}),
      });
      p.onDone(r.skipped.length ? `${r.created.length} marcações feitas; ${r.skipped.length} ficaram de fora (hora ocupada ou fora do horário).` : undefined);
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (!force && ['slot_taken', 'outside_schedule', 'resource_taken'].includes(code ?? '') && p.client.approve && await p.client.approve((e as Error).message)) { await submit(true); return; }
      setError((e as Error).message); setBusy(false);
    }
  };
  return (
    <Shell title="Nova marcação" onClose={p.onClose} footer={
      <button disabled={busy || !customer.name.trim() || !staffId} onClick={() => void submit()} className="h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">
        Marcar{total || minutes ? ` · ${minutes || total} min` : ''}
      </button>
    }>
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          {customer.id
            ? <div className="flex h-12 items-center justify-between rounded-xl bg-surface-2 px-4 font-semibold">{customer.name}<button aria-label="Tirar o cliente" onClick={() => setCustomer({ name: '', phone: null })}><X size={16} /></button></div>
            : <input autoFocus aria-label="Nome do cliente" placeholder="Nome do cliente" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="h-12 rounded-xl border border-base bg-surface-2 px-4" />}
          {p.pickCustomer && <button onClick={async () => { const c = await p.pickCustomer!(); if (c) setCustomer(c); }} className="h-12 rounded-xl border border-base px-4 font-medium">Cliente da ficha</button>}
          <input aria-label="Telefone (para o lembrete)" inputMode="tel" placeholder="Telefone (para o lembrete por WhatsApp)" value={customer.phone ?? ''} onChange={(e) => setCustomer({ ...customer, phone: e.target.value || null })} className="h-12 rounded-xl border border-base bg-surface-2 px-4 sm:col-span-2" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm font-medium">Dia<input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1.5 h-12 w-full rounded-xl border border-base bg-surface-2 px-3" /></label>
          <label className="text-sm font-medium">Hora<input type="time" step={900} aria-label="Hora" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5 h-12 w-full rounded-xl border border-base bg-surface-2 px-3" /></label>
          <label className="text-sm font-medium">Duração<input inputMode="numeric" aria-label="Duração em minutos" placeholder={`${total || 30} min`} value={minutes} onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ''))} className="mt-1.5 h-12 w-full rounded-xl border border-base bg-surface-2 px-3" /></label>
        </div>
        <fieldset>
          <legend className="mb-2 text-sm font-semibold">Profissional</legend>
          <div className="flex flex-wrap gap-2">
            {p.staff.map((x) => (
              <button key={x.id} aria-label={x.name} aria-pressed={staffId === x.id} onClick={() => setStaffId(x.id)} className={clsx('flex h-12 items-center gap-2 rounded-full border-2 py-1 pl-1 pr-4 font-medium', staffId === x.id ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base')}>
                <Dot s={x} />{x.name}
              </button>
            ))}
          </div>
        </fieldset>
        {!!p.resources?.length && (
          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Sala ou cadeira</legend>
            <div className="flex flex-wrap gap-2">
              <button aria-pressed={!resourceId} onClick={() => setResourceId(null)} className={clsx('h-12 rounded-full border-2 px-4 font-medium', !resourceId ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>Nenhuma</button>
              {p.resources.map((r) => <button key={r.id} aria-pressed={resourceId === r.id} onClick={() => setResourceId(r.id)} className={clsx('h-12 rounded-full border-2 px-4 font-medium', resourceId === r.id ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{r.name}</button>)}
            </div>
          </fieldset>
        )}
        <fieldset>
          <legend className="mb-2 text-sm font-semibold">Serviços</legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {doable.map((x) => {
              const on = chosen.includes(x.id);
              const o = own(x.id);
              return (
                <button key={x.id} aria-pressed={on} onClick={() => setChosen(on ? chosen.filter((c) => c !== x.id) : [...chosen, x.id])}
                  className={clsx('flex min-h-16 flex-col justify-between rounded-xl border-2 p-2.5 text-left text-sm', on ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base')}>
                  <span className="font-medium leading-tight">{x.shortName || x.name}</span>
                  <span className="num text-xs text-muted">{formatMoney(o?.price ?? x.unitPrice)} MT · {o?.durationMin ?? x.durationMin ?? 30} min</span>
                </button>
              );
            })}
          </div>
        </fieldset>
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm font-medium"><input type="checkbox" className="h-5 w-5" checked={repeat.on} onChange={(e) => setRepeat({ ...repeat, on: e.target.checked })} /><Repeat size={16} /> Repetir</label>
          {repeat.on && (
            <div className="flex flex-wrap items-center gap-2 pl-8 text-sm">
              de <select aria-label="De quantas em quantas semanas" value={repeat.everyWeeks} onChange={(e) => setRepeat({ ...repeat, everyWeeks: Number(e.target.value) })} className="h-11 rounded-lg border border-base bg-[var(--surface)] px-2">{[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}</select>
              em {repeat.everyWeeks === 1 ? 'semana' : 'semanas'},
              <select aria-label="Quantas vezes" value={repeat.times} onChange={(e) => setRepeat({ ...repeat, times: Number(e.target.value) })} className="h-11 rounded-lg border border-base bg-[var(--surface)] px-2">{[2, 3, 4, 6, 8, 12].map((n) => <option key={n} value={n}>{n}</option>)}</select> vezes
            </div>
          )}
          <input aria-label="Nota" placeholder="Nota (opcional): preferências, o que trazer…" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
        </div>
        {error && <p role="alert" className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
      </div>
    </Shell>
  );
}

/** Uma marcação: o resumo e as acções do estado dela (confirmar, chegou, cobrar, faltou, cancelar, lembrete). */
function AppointmentDialog(p: { a: Appointment; staff?: AgendaStaff; businessName: string; offline?: boolean; onCharge?: (a: Appointment) => void; onHealth?: (a: Appointment) => void; onClose: () => void; onChange: (patch: { status?: ApptStatus }) => Promise<void> }) {
  const a = p.a;
  const st = localMin(a.startsAt), en = localMin(a.endsAt);
  const total = a.services.reduce((s, x) => s.plus(x.price), dec(0));
  const when = `${longDate(localDate(new Date(a.startsAt)))} às ${hm(st)}`;
  const act = (label: string, status: ApptStatus, tone: 'primary' | 'ghost' | 'danger' = 'ghost') => (
    <button key={status} disabled={p.offline} onClick={() => void p.onChange({ status })}
      className={clsx('h-12 rounded-xl px-4 font-semibold disabled:opacity-40', tone === 'primary' ? 'bg-brand-700 text-white' : tone === 'danger' ? 'border border-base text-brand-700' : 'border border-base')}>{label}</button>
  );
  const next: Record<ApptStatus, React.ReactNode[]> = {
    booked: [act('Confirmar', 'confirmed'), act('Chegou', 'arrived', 'primary'), act('Faltou', 'no_show', 'danger'), act('Cancelar', 'cancelled', 'danger')],
    confirmed: [act('Chegou', 'arrived', 'primary'), act('Faltou', 'no_show', 'danger'), act('Cancelar', 'cancelled', 'danger')],
    arrived: [act('Começar', 'in_service'), act('Cancelar', 'cancelled', 'danger')],
    in_service: [],
    done: [], no_show: [act('Voltar a marcar', 'booked')], cancelled: [act('Voltar a marcar', 'booked')],
  };
  return (
    <Shell title={a.customerName} onClose={p.onClose} footer={
      <div className="flex flex-wrap gap-2">
        {p.onCharge && ['arrived', 'in_service', 'confirmed', 'booked'].includes(a.status) && (
          <button disabled={p.offline} onClick={() => p.onCharge!(a)} className="h-12 flex-1 rounded-xl bg-brand-700 px-4 font-bold text-white disabled:opacity-40">Cobrar{!total.isZero() ? ` · ${formatMoney(total.toFixed(2))} MT` : ''}</button>
        )}
        {next[a.status]}
      </div>
    }>
      <dl className="space-y-3 text-sm">
        <div className="flex items-center gap-3"><Clock size={18} className="text-muted" /><span><b>{when}</b> – {hm(en)} · {STATUS_LABEL[a.status]}</span></div>
        {p.staff && <div className="flex items-center gap-3"><Dot s={p.staff} /><span>{p.staff.name}</span></div>}
        <div className="rounded-xl bg-surface-2 p-3">
          {a.services.length ? a.services.map((x) => <div key={x.productId} className="num flex justify-between"><span>{x.description} · {x.durationMin} min</span><span>{formatMoney(x.price)} MT</span></div>) : <span className="text-muted">Sem serviços indicados</span>}
        </div>
        {a.notes && <p className="text-muted">Nota: {a.notes}</p>}
        {a.seriesId && <p className="flex items-center gap-2 text-muted"><Repeat size={14} /> Marcação repetida</p>}
        {p.onHealth && a.customerId && (
          <button onClick={() => p.onHealth!(a)} className="mr-2 inline-flex h-11 items-center gap-2 rounded-xl border border-base px-4 font-semibold"><HeartPulse size={18} className="text-brand-700" /> Ficha de saúde</button>
        )}
        {a.phone && (
          <a target="_blank" rel="noreferrer" href={waLink(a.phone, `Olá ${a.customerName}! Lembramos a sua marcação em ${p.businessName}: ${when}. Até já!`)}
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-base px-4 font-semibold text-green-700"><MessageCircle size={18} /> Lembrete por WhatsApp</a>
        )}
      </dl>
    </Shell>
  );
}
